import { FC, useEffect, useRef, useState } from 'react';
import { BarStackHorizontal } from '@visx/shape';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import { useTheme } from '@simplywallst/ui-core';
import useWindowSize from '@/hooks/useWindowSize';
import Label from '../Label';
import YAxis from '../YAxis';
import XAxis from '../XAxis';
import { PatternLines } from '@visx/pattern';
import { Key } from '@components/TimeSeries';
import { Legend, Dot, TooltipTitle } from './styled';
import { Column } from '@simplywallst/ui-core';
import Tooltip from '@components/Tooltip';
import { useServerEnv, Size } from '@/hooks/useServerEnv';

enum Labels {
  individualSharesBought = 'bought by individuals',
  companySharesBought = 'bought by companies',
  individualSharesSold = 'sold by individuals',
  companySharesSold = 'sold by companies',
}

const getCountSentence = (total, i, c) => {
  if (total === 0) return '';
  const individual = `${i} individual${i === 1 ? `` : `s`}`;
  const company = `${c} compan${c === 1 ? `y` : `ies`}`;
  const and = i > 0 && c > 0 ? ` and ` : ``;
  return ` by ${i > 0 ? individual : ``}${and}${c > 0 ? company : ``}`;
};

const getTooltip = (
  type: 'bought' | 'sold',
  total,
  individualCount,
  companyCount,
  totalValue
) => (
  <TooltipTitle>
    {`
    ${total.toLocaleString()} shares ${type}${getCountSentence(
      total,
      individualCount,
      companyCount
    )}.
    Valued at approx. ${totalValue}
    `}
  </TooltipTitle>
);

const series = ['0–3', '3–6', '6–9', '9–12'];
const buyKeys = ['individualSharesBought', 'companySharesBought'];
const sellKeys = ['individualSharesSold', 'companySharesSold'];
const patternSellId = 'insider-trading-sell-lines';
const patternBuyId = 'insider-trading-buy-lines';

export interface Entity {
  companySharesBought: number;
  companySharesSold: number;
  companyBuyCount: number;
  companySellCount: number;
  individualSharesBought: number;
  individualSharesSold: number;
  individualBuyCount: number;
  individualSellCount: number;
  totalBought: number;
  totalSold: number;
  totalBoughtValue: string;
  totalSoldValue: string;
}

interface Props {
  data: Entity[];
}

const InsiderTrading: FC<Props> = ({ data }) => {
  const theme = useTheme();
  const { width: screenWidth = 0 } = useWindowSize();
  const SVGRef = useRef<HTMLDivElement>(null);
  const serverEnv = useServerEnv();
  const breakPoints = {
    [Size.SM]: 379,
    [Size.LG]: 821,
  };
  const [chartWidth, setChartWidth] = useState(
    serverEnv.isServer ? breakPoints[serverEnv.size] : 100
  );
  const [yScalar, setYScalar] = useState<number>(theme.x9spacePx);

  useEffect(() => {
    const { current } = SVGRef;
    if (current) {
      const width = current.getBoundingClientRect().width;
      if (width > 0) {
        setChartWidth(width);
      }
      if (screenWidth <= theme.mdMaxRaw && yScalar !== theme.x12spacePx) {
        setYScalar(theme.x12spacePx);
      } else if (screenWidth > theme.mdMaxRaw && yScalar !== theme.x9spacePx) {
        setYScalar(theme.x9spacePx);
      }
    }
  }, [
    chartWidth,
    screenWidth,
    theme.mdMaxRaw,
    theme.x12spacePx,
    theme.x9spacePx,
    yScalar,
  ]);

  const yPadding = theme.x5spacePx;
  const xPadding = theme.x7spacePx;
  const barStackWidth = chartWidth - xPadding;
  const max = Math.max(
    ...data.map((n) => Math.max(n.totalBought, n.totalSold))
  );

  const yScale = scaleBand({
    domain: series,
    range: [0, yScalar * series.length],
  });

  const xScale = scaleLinear({
    domain: [0, max],
    range: [0, 1],
  });

  const buyColor = scaleOrdinal({
    domain: buyKeys,
    range: [theme.color.good, `url('#${patternBuyId}')`],
  });

  const sellColor = scaleOrdinal({
    domain: sellKeys,
    range: [theme.color.warning, `url('#${patternSellId}')`],
  });

  return (
    <div ref={SVGRef}>
      <svg
        width="100%"
        height={yScale.range()[1] + yPadding}
        shapeRendering="crispEdges"
      >
        <XAxis x={xPadding} width={barStackWidth} />
        <YAxis y={yPadding} yScale={yScale} series={series} />
        <svg
          x={xPadding}
          y={yPadding}
          width={barStackWidth / 2}
          height={yScale.range()[1]}
          shapeRendering="crispEdges"
        >
          <PatternLines
            id={patternSellId}
            height={5}
            width={5}
            stroke={theme.color.warning}
            strokeWidth={1}
            orientation={['diagonal']}
          />
          <BarStackHorizontal
            data={data}
            keys={sellKeys}
            y={() => '0'}
            xScale={xScale}
            yScale={yScale}
            color={sellColor}
          >
            {(barStacks) => {
              return barStacks.map((barStack) => {
                return barStack.bars.map(
                  ({ x, key, index, width, height, color, bar: { data } }) => (
                    <svg
                      y={index * height}
                      x={theme.x1spacePx}
                      width={barStackWidth / 2 - 16}
                      height={height}
                      key={`insider-trading--${barStack.key}-${index}`}
                      data-cy-id={`chart-insider-trading-${key}-${series[index]}`}
                    >
                      {key === sellKeys[1] && (
                        <Label
                          y={theme.x4spacePx}
                          type="sold"
                          shares={data.totalSold}
                          value={data.totalSoldValue}
                          height={height - theme.x3spacePx}
                          width={barStackWidth / 2 - 16}
                          individuals={data.individualSellCount}
                          companies={data.companySellCount}
                          stack={screenWidth <= theme.mdMaxRaw}
                        />
                      )}
                      <rect
                        x={`${100 - x * 100 - width * 100}%`}
                        y={theme.x1spacePx}
                        width={`${width * 100}%`}
                        height={theme.x2spacePx}
                        fill={color}
                      />
                      <Tooltip
                        title={getTooltip(
                          'sold',
                          data.totalSold,
                          data.individualSellCount,
                          data.companySellCount,
                          data.totalSoldValue
                        )}
                        placement="top"
                        offsetY={-16}
                      >
                        <rect
                          x="0"
                          y="0"
                          width="100%"
                          height="100%"
                          fill="transparent"
                        />
                      </Tooltip>
                    </svg>
                  )
                );
              });
            }}
          </BarStackHorizontal>
        </svg>
        <svg
          x={xPadding + barStackWidth / 2}
          y={yPadding}
          width={barStackWidth / 2}
          height={yScale.range()[1]}
          shapeRendering="crispEdges"
        >
          <PatternLines
            id={patternBuyId}
            height={5}
            width={5}
            stroke={theme.color.good}
            strokeWidth={1}
            orientation={['diagonal']}
          />
          <BarStackHorizontal
            data={data}
            keys={buyKeys}
            y={() => '0'}
            xScale={xScale}
            yScale={yScale}
            color={buyColor}
          >
            {(barStacks) => {
              return barStacks.map((barStack) =>
                barStack.bars.map(
                  ({ x, key, index, width, height, color, bar: { data } }) => (
                    <svg
                      y={index * height}
                      x={theme.x1spacePx}
                      width={barStackWidth / 2 - 16}
                      height={height}
                      key={`insider-trading--${barStack.key}-${index}`}
                      data-cy-id={`chart-insider-trading-${key}-${series[index]}`}
                    >
                      {key === buyKeys[1] && (
                        <Label
                          y={theme.x4spacePx}
                          type="bought"
                          shares={data.totalBought}
                          value={data.totalBoughtValue}
                          height={height - theme.x3spacePx}
                          width={barStackWidth / 2 - 16}
                          individuals={data.individualBuyCount}
                          companies={data.companyBuyCount}
                          stack={screenWidth <= theme.mdMaxRaw}
                        />
                      )}
                      <rect
                        x={`${x * 100}%`}
                        y={theme.x1spacePx}
                        width={`${width * 100}%`}
                        height={theme.x2spacePx}
                        fill={color}
                      />
                      <Tooltip
                        title={getTooltip(
                          'bought',
                          data.totalBought,
                          data.individualBuyCount,
                          data.companyBuyCount,
                          data.totalBoughtValue
                        )}
                        placement="top"
                        offsetY={-16}
                      >
                        <rect
                          x="0"
                          y="0"
                          width="100%"
                          height="100%"
                          fill="transparent"
                        />
                      </Tooltip>
                    </svg>
                  )
                )
              );
            }}
          </BarStackHorizontal>
        </svg>
        <rect
          x="0"
          y={yScale.range()[1] + yPadding - 1}
          width="100%"
          height="1"
          fill={theme.color.white}
          fillOpacity="0.5"
        />
      </svg>
      <Legend>
        <Column col={6}>
          {sellKeys.map((n) => (
            <Key
              key={`insider-trading-key-${n}`}
              color={n !== sellKeys[1] ? theme.color.warning : undefined}
            >
              {n !== sellKeys[0] && (
                <Dot width="8" height="8">
                  <circle
                    cx="4"
                    cy="4"
                    r="4"
                    fill={theme.color.warning}
                    fillOpacity="0.3"
                  />
                  <circle cx="4" cy="4" r="4" fill={sellColor(n)} />
                </Dot>
              )}
              {Labels[n]}
            </Key>
          ))}
        </Column>
        <Column col={6}>
          {buyKeys.map((n) => (
            <Key
              key={`insider-trading-key-${n}`}
              color={n !== buyKeys[1] ? theme.color.good : undefined}
            >
              {n !== buyKeys[0] && (
                <Dot width="8" height="8">
                  <circle
                    cx="4"
                    cy="4"
                    r="4"
                    fill={theme.color.good}
                    fillOpacity="0.3"
                  />
                  <circle cx="4" cy="4" r="4" fill={buyColor(n)} />
                </Dot>
              )}
              {Labels[n]}
            </Key>
          ))}
        </Column>
      </Legend>
    </div>
  );
};

export default InsiderTrading;
